import * as React from "react";
import { observer, inject } from "mobx-react";
import { computed, observable, action } from "mobx";
import { TripCostEstimatorHotelCost } from "../typings";
import {
  FlexTrackingInfo,
  Action,
  sendImmediateTrackEvent
} from "src/components/utility/analytics/FlexAnalyticsUtils";
import { NavigatorChart } from "./Navigator/Navigator";
import { Popover, PopoverProps } from "./Popover/Popover";
import {
  ChartDrawData,
  MonthChartProps,
  PopoverPropsCreator,
  ParamsSetChartBehaviour
} from "./typings";
import { Loader } from "./Loader/Loader";
import { formatText } from "bernie-l10n";

const flexTrackingInfoBarClick: FlexTrackingInfo = {
  moduleName: "tripCostEstimator",
  rfrr: "chart.bar",
  action: Action.CLICK
};

const l10n = {
  legend: (localizedName: string) => formatText("tripCostEstimator.graph.legend", localizedName)
};

// This component should be removed and replaced when UDS/Uitk create a graph component
@inject("analytics", "context", "routeStore")
@observer
export class MonthChart extends React.Component<MonthChartProps> {
  @observable
  private hasUserInteractionBar = false;

  @observable
  private popoverForceClose = false;

  @observable
  private popoverProps: PopoverProps = {
    containerWidth: 0,
    x: 0,
    y: 0,
    title: "",
    linkText: "",
    linkAction: () => null,
    isOpen: false
  };

  @observable
  private dataShownOnChart: TripCostEstimatorHotelCost[];

  @observable
  private graph: any;

  @computed
  public get isGraphRendered() {
    return !!(this.graph && this.graph.container);
  }

  @observable
  private graphSlide = 0;

  @computed
  public get getChartLabels(): string[] {
    return this.dataShownOnChart.map(({ abbrMonth, avgFourStarsCost, avgTwoStarsCost }) => {
      const hasEmptyMonthCost = !(avgFourStarsCost && avgTwoStarsCost);

      return `${abbrMonth + (hasEmptyMonthCost ? "*" : "")} `;
    });
  }

  @computed
  public get getChartValue(): number[][] {
    return [
      this.dataShownOnChart.map(item => item.avgTwoStarsCost),
      this.dataShownOnChart.map(
        item =>
          item.avgTwoStarsCost &&
          item.avgFourStarsCost &&
          item.avgFourStarsCost - item.avgTwoStarsCost
      )
    ];
  }

  @computed
  public get getMonthRangeString(): string {
    const labels = this.dataShownOnChart && this.dataShownOnChart.map(({ month }) => month);

    return labels ? `${labels[0]} - ${labels[labels.length - 1]}` : "";
  }

  @computed
  public get isPopoverShown(): boolean {
    return this.props.showPopover;
  }

  @computed
  public get shouldShowNavigation(): boolean {
    if (!this.dataShownOnChart) {
      return true;
    }

    return this.dataShownOnChart.length < this.props.data.length;
  }

  constructor(props: MonthChartProps) {
    super(props);

    this.changeMonthOnChart = this.changeMonthOnChart.bind(this);
    this.chartDidDraw = this.chartDidDraw.bind(this);
    this.closePopover = this.closePopover.bind(this, true);
    this.createGraph = this.createGraph.bind(this);
    this.createSendTrackBarClick = this.createSendTrackBarClick.bind(this);
    this.handleBarClick = this.handleBarClick.bind(this);
    this.setChartBehaviourWithPopover = this.setChartBehaviourWithPopover.bind(this);
    this.setSlide = this.setSlide.bind(this);
  }

  public componentDidMount() {
    this.setSlide(0);

    import(/* webpackChunkName: "chartist" */ "chartist").then(
      function(Chartist: any) {
        this.graph = this.createGraph(Chartist.Bar)(this.getChartLabels, this.getChartValue);
        this.graph.on("draw", this.chartDidDraw);

        this.props.handleRenderedGraph({ closePopover: this.closePopover.bind(this, true) });
      }.bind(this)
    );
  }

  @action
  public createGraph(Bar: any) {
    return (labels: string[], values: number[][]) => {
      return new Bar(
        `#${this.props.id}`,
        { labels, series: values },
        {
          low: this.props.low,
          high: this.props.high,
          axisY: {
            scaleMinSpace: 30,
            labelInterpolationFnc: (value: number) => `$${value}`
          },
          chartPadding: { right: 0, left: 0 },
          stackBars: true
        }
      );
    };
  }

  @action
  public setSlide(pageNumber: number) {
    const { data } = this.props;

    this.graphSlide = pageNumber;

    if (this.props.isMobile) {
      this.dataShownOnChart = data.slice(
        pageNumber * (data.length / 2),
        (pageNumber + 1) * (data.length / 2)
      );
    } else {
      this.dataShownOnChart = data; // show all 12 months on desktop
    }
  }

  @action
  public changeMonthOnChart(isRightArrow: boolean) {
    return (e: MouseEvent) => {
      e.stopPropagation();
      const pageNumber = Number(isRightArrow);
      this.setSlide(pageNumber);
      this.graph.update({ labels: this.getChartLabels, series: this.getChartValue });

      if (!this.isPopoverShown) {
        return;
      }

      if (this.hasUserInteractionBar) {
        const isOnChart = this.dataShownOnChart.some(
          item => item.month === this.popoverProps.title
        );

        if (isOnChart && !this.popoverForceClose) {
          this.popoverProps.isOpen = true;

          return;
        }
      }

      this.closePopover();
    };
  }

  @action
  public createPopoverProps({ containerWidth, x, y, item, linkAction }: PopoverPropsCreator) {
    this.popoverProps = {
      containerWidth,
      x,
      y,
      title: item ? `$${item.avgTwoStarsCost} - $${item.avgFourStarsCost}` : "",
      linkText: item ? item.month : "",
      linkAction,
      isOpen: true
    };
  }

  /**
   * When click on Bars, open the popover if feature flag 'popoverShown=true' is on URL,
   * Or redirect to HSR if this flag doesn't be there
   *
   * @param callbackWhenClickBar
   * @param barItemReferred
   */
  public handleBarClick(
    callbackWhenClickBar: () => void,
    barItemReferred: TripCostEstimatorHotelCost
  ) {
    return (event: MouseEvent & { target: SVGLineElement }) => {
      sendImmediateTrackEvent(flexTrackingInfoBarClick, this.props.analytics);
      if (!this.isPopoverShown) {
        callbackWhenClickBar();

        return;
      }
      const barElement = event.target;
      event.stopPropagation();

      const container = this.graph.container as HTMLDivElement;

      this.createPopoverProps({
        containerWidth: container.clientWidth,
        x: event.layerX,
        y: event.layerY,
        item: barItemReferred,
        linkAction: callbackWhenClickBar
      });

      const lines = Array.from(container.getElementsByTagName("line"));

      lines.map(lineSvg => {
        lineSvg.classList.remove("hover");
      });

      barElement.classList.add("hover");

      this.hasUserInteractionBar = true;
      this.popoverForceClose = false;
    };
  }

  @action
  public createSendTrackBarClick(barItemReferred: TripCostEstimatorHotelCost) {
    return () => this.props.onClick(barItemReferred);
  }

  @action
  public chartDidDraw({ element, seriesIndex, index }: ChartDrawData) {
    switch (seriesIndex) {
      case 0:
        element.addClass("hidden");
        break;

      case 1:
        const lineSvg = element._node;
        const barItemReferred = this.dataShownOnChart[index];
        const handlePopoverClick = this.createSendTrackBarClick(barItemReferred);

        if (this.isPopoverShown) {
          this.setChartBehaviourWithPopover({
            lineSvg,
            index,
            barItemReferred,
            handlePopoverClick
          });
        }

        lineSvg.addEventListener("click", this.handleBarClick(handlePopoverClick, barItemReferred));
        break;
    }
  }

  @action
  public setChartBehaviourWithPopover({
    lineSvg,
    index,
    barItemReferred,
    handlePopoverClick
  }: ParamsSetChartBehaviour) {
    const popoverTitle = this.popoverProps.title;
    const isSecondRendered = index === 1 && this.graphSlide === 0 && !popoverTitle;

    if (isSecondRendered) {
      const containerSVG = lineSvg.ownerSVGElement as SVGSVGElement;
      const lineBBOx = lineSvg.getBBox();
      this.createPopoverProps({
        containerWidth: containerSVG.clientWidth,
        x: lineBBOx.x,
        y: 0,
        item: barItemReferred,
        linkAction: handlePopoverClick
      });
    }

    if (this.popoverProps.linkText === barItemReferred.month) {
      lineSvg.classList.add("hover");
    }
  }

  @action
  public closePopover(force?: boolean) {
    const container = this.graph.container as HTMLDivElement;
    const lines = Array.from(container.getElementsByTagName("line"));

    lines.map(lineSvg => lineSvg.classList.remove("hover"));
    this.popoverProps.isOpen = false;
    if (force) {
      this.popoverForceClose = true;
    }
  }

  public render() {
    const { id } = this.props;
    const localizedName =
      (this.props.context && this.props.context.searchContext.location.localizedName) || "";

    const chartClassName = this.props.isMobile ? "chart" : "chart-desktop";

    return (
      <div className="MonthChart">
        <span className="legendCityName all-t-margin-three all-b-margin-six all-l-padding-four uitk-type-300">
          {l10n.legend(localizedName)}
        </span>
        <div className="chartContainer all-x-padding-zero">
          <Loader loading={!this.isGraphRendered} />
          {this.isPopoverShown && <Popover {...this.popoverProps} />}
          <div id={id} className={chartClassName} />
        </div>
        <NavigatorChart
          currentSlide={this.graphSlide}
          monthRange={this.getMonthRangeString}
          onChange={this.changeMonthOnChart}
          shouldShowNavigation={this.shouldShowNavigation}
        />
      </div>
    );
  }
}

export default MonthChart;
